import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import useApi from '../../services/useApi';
import { getAccessPrivilegeObject } from '../../components/commonfunctions/getAccessPrivilageObject';
import { access, getTotalPrivilegesCount } from '../../components/utils/access';
import dependentAccess from '../../components/utils/dependentAccess';
import { dependantRemoverObject } from '../../components/utils/dependentAccess';

interface AddAdminDetails {
  first_name: string;
  last_name: string;
  email_address: string;
  phone_number: string;
  house_no: string;
  market_block_no: string;
  floor_no: string;
  street_name: string;
  city_town: string;
  country: string;
  province: string;
  region: string;
  district: string;
  postal_code: string;
  sop_file: string;
  email_address_verify: string;
  phone_number_verify: string;
  access_privilege: string;
  country_code: string;
  is_super_admin: boolean;
  secondary_phone_numbers?: { country_code: string; phone_number: string }[] | null;
}

interface AddAdminLoader {
  email_address: boolean;
  phone_number: boolean;
}

interface AddAdminState {
  addAdminDetails: AddAdminDetails;
  add_loading: boolean;
  fileError: string | null;
  felidError: string[] | null;
  errors: Partial<Record<keyof AddAdminDetails, string>>;
  addAdminLoader: AddAdminLoader;
  disableAll: boolean;
  accessPrivilege: string[];
  accessPrivilegeEdit: string[];
  wallet_access: string[];
  wallet_access_edit: string[];
  editLoading: boolean;
  editData: AddAdminDetails;
  isVerifiedFirst: boolean;
}

interface ApiResponse {
  error: boolean;
  data: {
    first_name?: string;
    last_name?: string;
    email_address?: string;
    phone_number?: string;
    house_no?: string;
    market_block_no?: string;
    floor_no?: string;
    street_name?: string;
    city_town?: string;
    country?: string;
    province?: string;
    region?: string;
    district?: string;
    postal_code?: string;
    sop_file?: string;
    access_privileges?: string[];
    wallet_access?: string[];
    country_code: string;
    secondary_phone_numbers?: { country_code: string; phone_number: string }[] | null;
    is_super_admin?: boolean;
  } & Partial<AddAdminDetails>;
}

interface ErrorPayload {
  message: string;
}

const inputs: AddAdminDetails = {
  first_name: '',
  last_name: '',
  email_address: '',
  phone_number: '',
  house_no: '',
  market_block_no: '',
  floor_no: '',
  street_name: '',
  city_town: '',
  country: '',
  province: '',
  region: '',
  district: '',
  postal_code: '',
  sop_file: '',
  email_address_verify: '',
  phone_number_verify: '',
  access_privilege: '',
  country_code: '',
  is_super_admin: false,
  secondary_phone_numbers: null,
};

const loaders: AddAdminLoader = {
  email_address: false,
  phone_number: false,
};

const initialState: AddAdminState = {
  addAdminDetails: { ...inputs },
  add_loading: false,
  fileError: null,
  felidError: null,
  errors: {},
  addAdminLoader: { ...loaders },
  disableAll: false,
  accessPrivilege: [],
  wallet_access: [],
  editLoading: false,
  editData: { ...inputs },
  isVerifiedFirst: false,
  accessPrivilegeEdit: [],
  wallet_access_edit: [],
};

export const fetchAdminView = createAsyncThunk<ApiResponse, string, { rejectValue: ErrorPayload }>(
  'addAdmin/fetchAdminView',
  async (payload, { rejectWithValue }) => {
    try {
      const api = useApi();
      const ApiResponse = await api.getAPI(`${payload}`);
      return ApiResponse.data;
    } catch (error: unknown) {
      console.error('Error fetching list:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      return rejectWithValue({ message: errorMessage });
    }
  }
);

const addAdminSlice = createSlice({
  name: 'Add Admin',
  initialState,
  reducers: {
    resetAddAdmin: () => {
      return { ...initialState };
    },
    setUpdateAdminStates: (
      state,
      action: PayloadAction<{ key: string; value: string }>
    ) => {
      const { key, value } = action.payload;
      if (key in state.addAdminDetails) {
        state.addAdminDetails = {
          ...state.addAdminDetails,
          [key]: value
        };
        state.errors = {
          ...state.errors,
          [key]: ''
        };
      }
    },
    setAddAdminLoader: (
      state,
      action: PayloadAction<{ key: string; value: boolean }>
    ) => {
      const { key, value } = action.payload;
      if (key in state.addAdminLoader) {
        state.addAdminLoader = {
          ...state.addAdminLoader,
          [key]: value
        };
        state.disableAll = value;
      }
    },
    setIsVerifiedFirst: (state, action: PayloadAction<boolean>) => {
      state.isVerifiedFirst = action.payload;
    },
    setAddLoading: (state, action: PayloadAction<boolean>) => {
      state.add_loading = action.payload;
    },
    setFileError: (state, action: PayloadAction<string | null>) => {
      state.fileError = action.payload;
    },
    setAddAdminError: (state, action: PayloadAction<Partial<AddAdminDetails>>) => {
      // Filter out non-string values to maintain type compatibility
      const stringErrors: Partial<Record<keyof AddAdminDetails, string>> = {};
      Object.entries(action.payload).forEach(([key, value]) => {
        if (typeof value === 'string') {
          stringErrors[key as keyof AddAdminDetails] = value;
        }
      });
      state.errors = { ...state.errors, ...stringErrors };
    },
    setAccessPrivilege: (state, action: PayloadAction<string>) => {
      const element = action.payload;

      if (element === 'eTop Up' || element === 'Digital wallet') {
        state.wallet_access = state.wallet_access.includes(element)
          ? state.wallet_access.filter(item => item !== element)
          : [...state.wallet_access, element];
      } else {
        const newPrivileges = new Set(state.accessPrivilege);
        if (newPrivileges.has(element)) {
          newPrivileges.delete(element);
          if (element in dependantRemoverObject) {
            dependantRemoverObject[element as keyof typeof dependantRemoverObject].forEach(value => {
              if (newPrivileges.has(value)) {
                newPrivileges.delete(value);
              }
            });
          }
          // Set is_super_admin to false when privileges are removed
          state.addAdminDetails.is_super_admin = false;
        } else {
          newPrivileges.add(element);
          if (element in dependentAccess) {
            dependentAccess[element as keyof typeof dependentAccess].forEach(value => {
              if (!newPrivileges.has(value)) {
                newPrivileges.add(value);
              }
            });
          }
        }
        state.accessPrivilege = Array.from(newPrivileges);
        
        // Automatically check if all privileges are selected and set is_super_admin accordingly
        const totalPrivilegesCount = getTotalPrivilegesCount();
        if (state.accessPrivilege.length === totalPrivilegesCount) {
          state.addAdminDetails.is_super_admin = true;
        }
      }
    },
    setSelectAllAccessPrivilege: (state, action: PayloadAction<boolean>) => {
      const selectAll = action.payload;
      const access = getAccessPrivilegeObject();

      state.accessPrivilege = selectAll
        ? Object.values(access).flat()
        : [];
      
      // Set is_super_admin based on whether all privileges are selected
      state.addAdminDetails.is_super_admin = selectAll;
    },
    setAddAdminFelidError: (state, action: PayloadAction<string | null>) => {
      if (action.payload !== null) {
        state.felidError = state.felidError
          ? [...state.felidError, action.payload]
          : [action.payload];
      }
    },
    removeAdminFieldError: (state, action: PayloadAction<string>) => {
      if (state.felidError) {
        state.felidError = state.felidError.filter(id => id !== action.payload);
      }
    },
    setAdminSecondaryPhoneNumbers: (state, action: PayloadAction<{ country_code: string; phone_number: string }[] | null>) => {
      state.addAdminDetails.secondary_phone_numbers = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminView.pending, (state) => {
        state.disableAll = true;
        state.editLoading = true;
      })
      .addCase(fetchAdminView.fulfilled, (state, action: PayloadAction<ApiResponse>) => {
        state.disableAll = false;
        if (!action.payload.error) {
          const { data } = action.payload;

          const dataObj: Partial<AddAdminDetails> = {
            first_name: data.first_name || '',
            last_name: data.last_name || '',
            email_address: data.email_address || '',
            phone_number: `${data.country_code.split('+')[1]}` + `${data.phone_number || ''}`,
            house_no: data.house_no || '',
            market_block_no: data.market_block_no || '',
            floor_no: data.floor_no || '',
            street_name: data.street_name || '',
            city_town: data.city_town || '',
            country: data.country || '',
            province: data.province || '',
            region: data.region || '',
            district: data.district || '',
            postal_code: data.postal_code || '',
            sop_file: data.sop_file || '',
            email_address_verify: data.email_address ? 'true' : 'false',
            phone_number_verify: data.phone_number ? 'true' : 'false',
            country_code: data.country_code?.split('+')[1] || '',
            access_privilege: data.access_privileges?.join(',') || '',
            is_super_admin: data.is_super_admin ? true : false,
            secondary_phone_numbers: data.secondary_phone_numbers || null,
          };

          state.addAdminDetails = {
            ...state.addAdminDetails,
            ...dataObj,
          };
          state.editData = {
            ...state.editData,
            ...dataObj,
          };
          state.editLoading = false;

          const privileges: string[] = [];
          if (data.access_privileges?.includes('*')) {
            Object.values(access).forEach((section) => {
              Object.keys(section).forEach((action) => {
                privileges.push(action);
              });
            });
          } else {
            Object.values(access).forEach((section) => {
              Object.entries(section).forEach(([action, id]) => {
                if (data.access_privileges?.includes(id)) {
                  privileges.push(action);
                }
              });
            });
          }
          state.accessPrivilege = privileges;
          state.isVerifiedFirst = true;
          state.accessPrivilegeEdit = privileges;
          state.wallet_access = data.wallet_access?.includes('etop_up') ? ['eTop Up'] : [];
          state.wallet_access_edit = data.wallet_access?.includes('etop_up') ? ['eTop Up'] : [];

          Object.keys(dataObj).forEach((key) => {
            state.errors[key as keyof AddAdminDetails] = '';
          });
        } else {
          console.error('Error in API response:', action.payload);
        }
      })
      .addCase(fetchAdminView.rejected, (state, action: PayloadAction<ErrorPayload | undefined>) => {
        state.editLoading = false;
        console.error('Error fetching details:', action.payload?.message);
      });
  }
});

export const {
  setIsVerifiedFirst,
  resetAddAdmin,
  setUpdateAdminStates,
  setAddLoading,
  setAddAdminLoader,
  setFileError,
  setAddAdminError,
  setAccessPrivilege,
  setSelectAllAccessPrivilege,
  setAddAdminFelidError,
  removeAdminFieldError,
  setAdminSecondaryPhoneNumbers,
} = addAdminSlice.actions;

export default addAdminSlice.reducer;